// Shared crisis / safety detection.
// Used by Journal, Dream Journal, Community, and Mood Check-in to surface a
// safety dialog AND log an alert for admin review.
//
// Keyword/regex-based for speed and zero cost. Returns a graded risk level so
// admins can triage:
//   - critical: imminent intent ("I'm going to kill myself tonight", "have a plan")
//   - red:      explicit suicidal ideation or self-harm ("kill me", "kill myself", "want to die")
//   - orange:   strong hopelessness / "better off dead" / "can't go on"
//   - yellow:   distress signals ("hopeless", "in crisis", past-tense self-harm)
//   - none:     no match
//
// This is a soft signal — it never blocks the user.

export type CrisisRiskLevel = "yellow" | "orange" | "red" | "critical";

interface PatternRule {
  pattern: RegExp;
  level: CrisisRiskLevel;
  term: string;
}

const RULES: PatternRule[] = [
  // ── CRITICAL: imminent intent / plan / means ───────────────────────────
  { level: "critical", term: "plan to kill self",      pattern: /\b(have|got|made)\s+(a\s+)?plan\b.{0,40}\b(kill|die|end)\b/i },
  { level: "critical", term: "have a plan to end life",pattern: /\b(have|got)\s+a\s+plan\s+to\s+end\b/i },
  { level: "critical", term: "tonight/today end",      pattern: /\b(tonight|today|now|right\s+now)\b.{0,40}\b(end\s+(it|my\s+life)|kill\s+my\s*self|die)\b/i },
  { level: "critical", term: "going to kill self",     pattern: /\b(i\s+am|i'?m|im|going)\s+to\s+kill\s+my\s*self\b/i },
  { level: "critical", term: "about to end life",      pattern: /\b(about\s+to|ready\s+to)\s+(end\s+(it|my\s+life)|kill\s+my\s*self|die)\b/i },
  { level: "critical", term: "suicide note",           pattern: /\bsuicide\s+note\b/i },
  { level: "critical", term: "pills ready",            pattern: /\b(have|got)\s+(the\s+)?pills\s+ready\b/i },
  { level: "critical", term: "have a weapon",          pattern: /\b(have|got)\s+(a\s+)?(weapon|gun|knife|rope)\b/i },
  { level: "critical", term: "goodbye forever",        pattern: /\bgood-?bye\s+(forever|everyone|world)\b/i },
  { level: "critical", term: "last message",           pattern: /\b(this\s+is\s+)?my\s+last\s+(message|post|note|words?)\b/i },
  { level: "critical", term: "saying goodbye",         pattern: /\b(i\s+am|i'?m|im)\s+saying\s+good-?bye\b/i },
  { level: "critical", term: "won't see me tomorrow",  pattern: /\b(nobody|no\s+one)\s+will\s+see\s+me\s+(tomorrow|again)\b/i },
  { level: "critical", term: "won't be here tomorrow", pattern: /\bwon'?t\s+be\s+(here|alive)\s+(tomorrow|much\s+longer)\b/i },

  // ── RED: explicit suicidal ideation / self-harm ────────────────────────
  { level: "red", term: "suicidal/suicide",            pattern: /\bsuicid(e|al)\b/i },
  // Typo-tolerant variants (sucidal, suicde, suiside, suicidial, suidical, etc.)
  { level: "red", term: "suicidal (typo)",             pattern: /\b(sui?[cs]i?[dt]a?[il]e?|suic?de?|sui?side|suicidial|suidical|suicdal|sucidial)\b/i },
  { level: "red", term: "feel suicidal",               pattern: /\b(feel(ing)?|am|i'?m|im)\s+(very\s+|really\s+|so\s+)?(suicid\w*|sui?ci?dal?|sucid\w*|suiside\w*)\b/i },
  { level: "red", term: "kms",                         pattern: /\bkms\b/i },
  { level: "red", term: "want to kill me/myself",      pattern: /\b(want(ed|ing)?|going|tried|tempted|plan(ning|ned)?)\s+to\s+kill\s+(me|my\s*self)\b/i },
  { level: "red", term: "kill me/myself",              pattern: /\bkill(ing|ed)?\s+(me|my\s*self)\b/i },
  { level: "red", term: "end my life",                 pattern: /\b(take|took|taking|end(ing|ed)?)\s+my\s+(own\s+)?life\b/i },
  { level: "red", term: "end it all",                  pattern: /\bend(ing)?\s+(it\s+all|everything)\b/i },
  { level: "red", term: "want to die",                 pattern: /\b(want(ing|ed)?|going|wish(ing|ed)?)\s+to\s+die\b/i },
  { level: "red", term: "i wish/wanted to die",        pattern: /\bi\s+(want|wish|wanted)\s+to\s+die\b/i },
  { level: "red", term: "don't want to live anymore",  pattern: /\b(don'?t|do not)\s+want\s+to\s+live\s+(any\s*more|anymore)\b/i },
  { level: "red", term: "cutting myself",              pattern: /\bcut(ting)?\s+my\s*self\b/i },
  { level: "red", term: "burn myself",                 pattern: /\bburn(ing|ed|t)?\s+my\s*self\b/i },
  { level: "red", term: "injure myself",               pattern: /\b(injur(e|ing|ed)|wound(ing|ed)?)\s+my\s*self\b/i },
  { level: "red", term: "hurt/harm myself",            pattern: /\b(hurt|harm)(ing)?\s+my\s*self\b/i },
  { level: "red", term: "self-harm",                   pattern: /\b(self[\s-]?harm|self[\s-]?injur(y|e))\b/i },
  { level: "red", term: "deserve pain",                pattern: /\b(i\s+)?deserve\s+(pain|to\s+(suffer|hurt|die))\b/i },
  { level: "red", term: "overdose",                    pattern: /\boverdos(e|ing|ed)\b/i },

  // ── ORANGE: hopelessness, "better off dead", can't go on, abuse/safety ─
  { level: "orange", term: "don't want to live",       pattern: /\b(don'?t|do not|didn'?t|did not)\s+want\s+to\s+(live|be\s+here|exist|wake\s+up)\b/i },
  { level: "orange", term: "no reason to live",        pattern: /\bno\s+(reason|point|will)\s+to\s+(live|go\s+on|continue)\b/i },
  { level: "orange", term: "can't go on",              pattern: /\bcan(no|')?t\s+go\s+on\b/i },
  { level: "orange", term: "cannot go on",             pattern: /\bcannot\s+go\s+on\b/i },
  { level: "orange", term: "better off dead/gone",     pattern: /\bbetter\s+off\s+(dead|without\s+me|gone)\b/i },
  { level: "orange", term: "give up on life",          pattern: /\b(give|giving|gave)\s+up\s+on\s+(life|everything)\b/i },
  { level: "orange", term: "being abused",             pattern: /\b(being|been|am|currently)\s+abused\b/i },
  { level: "orange", term: "i am being hurt",          pattern: /\b(i\s+am|i'?m|im)\s+being\s+(hurt|hit|beaten|attacked)\b/i },
  { level: "orange", term: "domestic violence",        pattern: /\bdomestic\s+(violence|abuse)\b/i },
  { level: "orange", term: "sexual abuse/assault",     pattern: /\bsexual(ly)?\s+(abuse|abused|assault(ed)?)\b/i },
  { level: "orange", term: "i am unsafe",              pattern: /\b(i\s+am|i'?m|im)\s+(not\s+safe|unsafe)\b/i },
  { level: "orange", term: "i am trapped",             pattern: /\b(i\s+am|i'?m|im|feel)\s+trapped\b/i },
  { level: "orange", term: "fear for my safety",       pattern: /\b(fear|afraid|scared)\s+for\s+my\s+(safety|life)\b/i },

  // ── YELLOW: distress signals worth monitoring ──────────────────────────
  { level: "yellow", term: "in crisis",                pattern: /\bin\s+crisis\b/i },
  { level: "yellow", term: "hopeless",                 pattern: /\bhopeless(ness)?\b/i },
  { level: "yellow", term: "severe depression",        pattern: /\bsevere(ly)?\s+depress(ed|ion)\b/i },
];

const RANK: Record<CrisisRiskLevel, number> = {
  yellow: 1,
  orange: 2,
  red: 3,
  critical: 4,
};

export interface CrisisAssessment {
  detected: boolean;
  level: CrisisRiskLevel | null;
  terms: string[];
}

/**
 * Run the rule set against text and return the highest matched risk level
 * plus the matched terms (for admin context).
 */
export function assessCrisis(text: string): CrisisAssessment {
  if (!text || !text.trim()) {
    return { detected: false, level: null, terms: [] };
  }
  const hits: PatternRule[] = [];
  for (const rule of RULES) {
    if (rule.pattern.test(text)) hits.push(rule);
  }
  if (hits.length === 0) return { detected: false, level: null, terms: [] };
  const highest = hits.reduce<CrisisRiskLevel>(
    (acc, h) => (RANK[h.level] > RANK[acc] ? h.level : acc),
    hits[0].level,
  );
  const terms = Array.from(new Set(hits.map((h) => h.term)));
  return { detected: true, level: highest, terms };
}

/** Backwards-compatible boolean shim. */
export function detectCrisis(text: string): boolean {
  return assessCrisis(text).detected;
}

/** Short label per risk level for badges. */
export const RISK_LABEL: Record<CrisisRiskLevel, string> = {
  yellow: "Monitor",
  orange: "Concern",
  red: "High risk",
  critical: "Immediate review",
};
